<template>
  <div v-if="loading" class="promo_animate">
    <svg
      class="animate"
      viewBox="0 0 200 200"
      width="200"
      height="200"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="animate-l-g1" x1="1" y1="0.5" x2="0" y2="0.5">
          <stop offset="0%" stop-color="hsl(313,90%,55%)" />
          <stop offset="100%" stop-color="hsl(223,90%,55%)" />
        </linearGradient>
        <linearGradient id="animate-l-g2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="hsl(313,90%,55%)" />
          <stop offset="100%" stop-color="hsl(223,90%,55%)" />
        </linearGradient>
      </defs>
      <circle
        class="animate__ring"
        cx="100"
        cy="100"
        r="82"
        fill="none"
        stroke="url(#animate-l-g1)"
        stroke-width="36"
        stroke-dasharray="0 257 1 257"
        stroke-dashoffset="0.01"
        stroke-linecap="round"
        transform="rotate(-90,100,100)"
      />
      <line
        class="animate__ball"
        stroke="url(#animate-l-g2)"
        x1="100"
        y1="18"
        x2="100.01"
        y2="182"
        stroke-width="36"
        stroke-dasharray="1 165"
        stroke-linecap="round"
      />
    </svg>
  </div>
  <PromoSlug
    v-if="pageInfo && pageInfo.blocks && !loading"
    :page-info="pageInfo"
  />
</template>

<script lang="ts" setup>
import type {
  BlockRequest,
  MinimalOfferItem,
  ModelItem,
  PromoBlock,
  PromoPage,
} from '~/interfaces/api';
import { API } from '~/api/api';
import { useGlobalStore } from '~/stores/global';

const route = useRoute();
const globalStore = useGlobalStore();
const getUrlPage = route?.params?.slug;
const baseURL = `/static/collections/${getUrlPage}.json`;
const pageInfo = ref<PromoPage | null>(null);
const loading = ref(true);
onBeforeMount(async () => {
  try {
    loading.value = true;
    const res = await fetch(`${baseURL}`);
    if (res.ok) {
      const setsData = await res.json();
      setsData.blocks = await loadItemsFromBlocksComponents(setsData?.blocks);
      pageInfo.value = setsData;
    }
  } catch {
    navigateTo('/');
  } finally {
    loading.value = false;
  }
});
const makeRequestForItems = async (req: BlockRequest) => {
  const { endpoint: url, method, params, body: data } = req;
  const res = await API({
    method,
    url,
    params: {
      ...params,
      ...(globalStore.region?.id && { region_id: globalStore.region.id }),
      ...(globalStore.sem && { sem: globalStore.sem }),
      ...(globalStore.device && { device: globalStore.device }),
    },
    ...(data && { data }),
  });
  return res.data;
};

const setComponentItems = async (reqInfo: BlockRequest[]) => {
  if (reqInfo) {
    let items: (ModelItem | MinimalOfferItem)[] = [];
    const itemsFromApi = await Promise.all(
      reqInfo.map(async (req) => await makeRequestForItems(req))
    );
    itemsFromApi.forEach((item) => {
      if (item?.items) items.push(...item.items);
      if (item?.list) items.push(...item.list);
      if (!item?.items && !item?.list) items.push(...item);
    });

    items = items.map((item) => {
      if (!item?.type) {
        item.type = 'model';
      }
      return item;
    });
    return items;
  }
  return [];
};

const loadItemsFromApi = async (block: PromoBlock) => {
  if (!block?.api || !block?.props?.data) return block;
  let items = await setComponentItems(block.api);

  // костыль для https://jira.price-co.ru/browse/PRICE-11041
  // добавлено условие отображения блока conditions
  // чтобы не вносить изменения в компонент, очищаем items
  if (block.conditions) {
    if (
      block.conditions.region_id &&
      block.conditions.region_id !== globalStore.region.id
    ) {
      items = [];
    }
  }

  return items?.length
    ? {
        ...block,
        props: { ...block.props, data: { ...block.props.data, items } },
      }
    : block;
};

async function loadItemsFromBlocksComponents(blocks: PromoBlock[]) {
  if (!blocks?.length) return [];
  return await Promise.all(
    blocks.map(async (block: PromoBlock) => await loadItemsFromApi(block))
  );
}
</script>
